import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import img from '../../assests/images/Images';
import CtaButton from '../../components/common/CtaButton';
import HeroSlider from './components/HeroSlider';
import ServiceCardsSection from './components/ServiceCardsSection';
import WhoWeAreSection from './components/WhoWeAreSection';
import HowWeWorkSection from './components/HowWeWorkSection';
import IndustriesSection from './components/IndustriesSection';


/* ─── Rotating Hero Header Text Switcher ─── */
const ROTATING_TEXTS = [
  { text: 'Scale Custom Software.', color: '#4caf50' },
  { text: 'Elevate User Experience.', color: '#f97316' },
  { text: 'Run Better Businesses.', color: '#1a2f6e' },
  { text: 'Drive Digital Growth.', color: '#0d9488' },
];

const RotatingHeroText = () => {
  const [index, setIndex] = useState(0);
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    const timer = setInterval(() => {
      setVisible(false);
      setTimeout(() => {
        setIndex((prev) => (prev + 1) % ROTATING_TEXTS.length);
        setVisible(true);
      }, 350);
    }, 2800);

    return () => clearInterval(timer);
  }, []);

  const current = ROTATING_TEXTS[index];

  return (
    <span
      style={{
        display: 'inline-block',
        color: current.color,
        fontWeight: 600,
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(-12px)',
        transition: 'opacity 0.4s cubic-bezier(0.16, 1, 0.3, 1), transform 0.4s cubic-bezier(0.16, 1, 0.3, 1), color 0.4s ease',
        willChange: 'opacity, transform',
      }}
    >
      {current.text}
    </span>
  );
};

/* ─── Continuous Letter Reveal Heading Component (Infinite Loop) ─── */
export const LetterRevealHeading = ({
  text = 'Accelerate Your Business with Custom Software & Marketing.',
  className = '',
  style = {},
  tag: Tag = 'h1',
  id = '',
  typeSpeed = 45,
  deleteSpeed = 22,
  pauseTime = 3200,
}) => {
  const [displayedCount, setDisplayedCount] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    let timer;

    if (!isDeleting && displayedCount < text.length) {
      timer = setTimeout(() => {
        setDisplayedCount((prev) => prev + 1);
      }, typeSpeed);
    } else if (!isDeleting && displayedCount === text.length) {
      timer = setTimeout(() => {
        setIsDeleting(true);
      }, pauseTime);
    } else if (isDeleting && displayedCount > 0) {
      timer = setTimeout(() => {
        setDisplayedCount((prev) => prev - 1);
      }, deleteSpeed);
    } else if (isDeleting && displayedCount === 0) {
      timer = setTimeout(() => {
        setIsDeleting(false);
      }, 500);
    }

    return () => clearTimeout(timer);
  }, [displayedCount, isDeleting, text, typeSpeed, deleteSpeed, pauseTime]);

  const currentText = text.slice(0, displayedCount);
  const words = currentText.split(' ');

  return (
    <Tag id={id} className={className} style={{ position: 'relative', minHeight: '2.4em', ...style }}>
      {words.map((word, wIdx) => (
        <span key={wIdx} style={{ display: 'inline-block', whiteSpace: 'nowrap' }}>
          {word.split('').map((char, cIdx) => (
            <span
              key={cIdx}
              style={{
                display: 'inline-block',
                animation: 'siplCharPulse 0.15s ease-out forwards',
              }}
            >
              {char}
            </span>
          ))}
          {wIdx < words.length - 1 && <span>&nbsp;</span>}
        </span>
      ))}
      <span
        style={{
          display: 'inline-block',
          width: '3px',
          height: '0.82em',
          backgroundColor: '#0284c7',
          marginLeft: '4px',
          verticalAlign: 'baseline',
          animation: 'siplBlink 0.8s infinite',
        }}
      />
    </Tag>
  );
};

/* ─── Mouse Parallax Hook (Lightweight RAF, zero React re-renders) ─── */
const useMouseParallax = (strength = 10) => {
  const ref = useRef(null);
  const rafId = useRef(null);

  useEffect(() => {
    const onMove = (e) => {
      if (rafId.current) return;
      rafId.current = requestAnimationFrame(() => {
        rafId.current = null;
        if (!ref.current) return;
        const rect = ref.current.getBoundingClientRect();
        const cx = rect.left + rect.width / 2;
        const cy = rect.top + rect.height / 2;
        const dx = ((e.clientX - cx) / rect.width) * strength;
        const dy = ((e.clientY - cy) / rect.height) * strength;
        ref.current.style.setProperty('--mp-x', `${dx.toFixed(2)}px`);
        ref.current.style.setProperty('--mp-y', `${dy.toFixed(2)}px`);
      });
    };

    window.addEventListener('mousemove', onMove, { passive: true });
    return () => {
      window.removeEventListener('mousemove', onMove);
      if (rafId.current) cancelAnimationFrame(rafId.current);
    };
  }, [strength]);

  return { ref };
};

/* ─── Scroll Reveal Hook (Native IntersectionObserver only) ─── */
const useScrollReveal = () => {
  useEffect(() => {
    const isReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const elements = document.querySelectorAll('.sipl-reveal, .sipl-reveal-ltr, .sipl-reveal-rtl, .sipl-reveal-ttb, .sipl-reveal-btt');

    if (isReduced) {
      elements.forEach((el) => el.classList.add('sipl-revealed'));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('sipl-revealed');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.05, rootMargin: '0px 0px -20px 0px' }
    );

    elements.forEach((el) => observer.observe(el));

    return () => {
      observer.disconnect();
    };
  }, []);
};

/* ─── Continuous Scroll Marquee Strip ─── */
const MARQUEE_ITEMS = [
  'Custom Web Apps',
  'Enterprise ERP & CRM',
  'Intuitive UI/UX Design',
  'Cloud Infrastructure',
  'Digital Growth Strategy',
  'HRM & Workforce Tools',
  'LMS Platforms',
  'API & System Integration',
];

const ScrollMarqueeStrip = () => {
  return (
    <section className="sipl-marquee-container" aria-label="Core Capabilities">
      <div className="d-flex align-items-center">
        <span className="fw-bold text-uppercase small text-muted px-4 tracking-wider" style={{ letterSpacing: '0.08em', flexShrink: 0 }}>
          What We Specialize In
        </span>
        <div style={{ overflow: 'hidden', width: '100%' }}>
          <div className="sipl-marquee-track">
            {[...MARQUEE_ITEMS, ...MARQUEE_ITEMS, ...MARQUEE_ITEMS].map((item, idx) => (
              <span
                key={`${item}-${idx}`}
                className="badge bg-white text-dark border px-3.5 py-2 rounded-pill fw-semibold shadow-sm"
                style={{ fontSize: '0.84rem', color: '#1e293b', borderColor: '#cbd5e1' }}
              >
                <span style={{ color: '#4caf50', marginRight: '6px' }}>•</span>
                {item}
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};


/* ─── Static Data (Human-Refined Copy) ─── */

// eslint-disable-next-line no-unused-vars
const WHY_POINTS = [
  {
    num: '01',
    title: 'Plain-English Communication',
    desc: 'No confusing tech jargon. We speak clearly about requirements, progress, and timelines so you always know where things stand.',
    badgeClass: 'sipl-bg-navy',
  },
  {
    num: '02',
    title: 'Direct Access to Engineers',
    desc: 'You work directly with the developers and designers building your application—not passed around between account managers.',
    badgeClass: 'sipl-bg-green',
  },
  {
    num: '03',
    title: 'Predictable, Transparent Scoping',
    desc: 'Clear project scope, transparent pricing, and regular milestone demos so there are no surprises at release time.',
    badgeClass: 'sipl-bg-teal',
  },
  {
    num: '04',
    title: 'Production-Ready Engineering',
    desc: 'We write clean, modular, and thoroughly tested code that is ready for real traffic from day one.',
    badgeClass: 'sipl-bg-orange',
  },
  {
    num: '05',
    title: 'Focus on Practical ROI',
    desc: 'We recommend tools and architecture that make commercial sense for your current scale and growth plans.',
    badgeClass: 'sipl-bg-navy',
  },
  {
    num: '06',
    title: 'Post-Launch Partnership',
    desc: 'We stay on board after release to handle updates, feature enhancements, and server maintenance as your business scales.',
    badgeClass: 'sipl-bg-green',
  },
];


/* ─── HERO SECTION ────────────────────────────────────────────── */
const HeroSection = () => {

  const { ref } = useMouseParallax(12);

  return (
    <section
      ref={ref}
      aria-labelledby="hero-heading"
      className="py-3 border-bottom sipl-section-transparent sipl-hero"
      style={{
        position: 'relative',
        overflow: 'hidden',
        background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 60%, #ffffff 100%)',
        minHeight: 'calc(100vh - 80px)',
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <div className="container-fluid px-3 px-sm-4 px-lg-5 py-2" style={{ position: 'relative', zIndex: 1, maxWidth: '1680px' }}>
        <div className="row align-items-center g-4 g-xl-5">

          {/* Left Column Text Content */}
          <div className="col-12 col-lg-5">
            <span
              className="text-uppercase fw-bold d-block mb-2 sipl-reveal-ttb"
              style={{ color: '#0284c7', fontSize: '0.8rem', letterSpacing: '0.12em', '--gr-delay': '120ms' }}
            >
              SOFTWARE ENGINEERING & DIGITAL MARKETING
            </span>

            <h1
              id="hero-heading"
              className="mb-3 sipl-reveal-ttb"
              style={{
                fontSize: 'clamp(1.8rem, 2.85vw, 2.45rem)',
                letterSpacing: '-0.025em',
                lineHeight: 1.22,
                color: '#0f172a',
                fontWeight: 600,
                '--gr-delay': '180ms',
              }}
            >
              <span className="d-block" style={{ color: '#0f172a', fontWeight: 600 }}>
                Software Engineered for
              </span>
              <span className="d-block" style={{ color: '#0f172a', fontWeight: 600 }}>
                Real Business Growth.
              </span>
              <span className="d-block mt-1" style={{ minHeight: '1.25em' }}>
                <RotatingHeroText />
              </span>
            </h1>

            <p
              className="mb-4 sipl-reveal-ttb"
              style={{
                fontSize: '0.95rem',
                lineHeight: 1.65,
                maxWidth: 520,
                color: '#475569',
                '--gr-delay': '320ms',
              }}
            >
              Seikural Innovation helps businesses build custom web applications, streamline internal workflows, and create reliable digital products designed for real daily use.
            </p>

            <div
              className="d-flex flex-wrap align-items-center gap-3 mb-4 sipl-reveal-ttb"
              style={{ '--gr-delay': '460ms' }}
            >
              <CtaButton to="/contact" variant="primary">
                Discuss Your Project
              </CtaButton>

              <CtaButton to="/services/web-development" variant="secondary">
                Explore Services
              </CtaButton>
            </div>

            <div
              className="sipl-reveal-ttb"
              style={{
                fontSize: '0.84rem',
                color: '#64748b',
                fontWeight: 500,
                letterSpacing: '-0.01em',
                '--gr-delay': '600ms',
              }}
            >
              Custom Web Apps · Enterprise Systems · UI/UX Design · Cloud DevOps · Digital Marketing
            </div>
          </div>

          {/* Right Column Header Slider */}
          <div
            className="col-12 col-lg-7 d-flex align-items-center justify-content-center sipl-reveal-ttb"
            style={{ '--gr-delay': '350ms' }}
          >
            <HeroSlider />
          </div>

        </div>
      </div>
    </section>
  );
};

/* ─── MAIN HOME COMPONENT ────────────────────────────────────── */
const Home = () => {
  useScrollReveal();

  return (
    <div className="sipl-home-page-wrapper">
      {/* Ambient background glow spots (Logo color palette) */}
      <div className="sipl-ambient-glow" style={{ position: 'absolute', pointerEvents: 'none', top: '1%', left: '-4%', width: 550, height: 550, background: 'radial-gradient(circle, rgba(26,47,110,0.045) 0%, transparent 70%)' }} />
      <div className="sipl-ambient-glow" style={{ position: 'absolute', pointerEvents: 'none', top: '22%', right: '-6%', width: 600, height: 600, background: 'radial-gradient(circle, rgba(13,148,136,0.04) 0%, transparent 70%)' }} />
      <div className="sipl-ambient-glow" style={{ position: 'absolute', pointerEvents: 'none', top: '50%', left: '-5%', width: 500, height: 500, background: 'radial-gradient(circle, rgba(76,175,80,0.035) 0%, transparent 70%)' }} />
      <div className="sipl-ambient-glow" style={{ position: 'absolute', pointerEvents: 'none', top: '78%', right: '-4%', width: 550, height: 550, background: 'radial-gradient(circle, rgba(249,115,22,0.025) 0%, transparent 70%)' }} />

      {/* SECTION 1: HERO */}
      <HeroSection />

      {/* SECTION 2: SCROLL MARQUEE STRIP */}
      <ScrollMarqueeStrip />

      {/* SECTION 3: WHO WE ARE */}
      <WhoWeAreSection />


      {/* SECTION 4: CORE SERVICES */}
      <section className="py-5 sipl-section-tint-translucent" aria-label="Services" style={{ position: 'relative', zIndex: 1 }}>
        <div className="container py-4">

          <div className="row mb-5 justify-content-center text-center">
            <div className="col-12 col-lg-8 sipl-reveal-ttb">
              <span className="text-uppercase fw-bold sipl-text-teal tracking-wider small d-block mb-2" style={{ letterSpacing: '0.12em' }}>
                OUR SERVICES
              </span>
              <h2 className="display-6 fw-bold text-dark mb-3">
                End-to-end software engineering & product design.
              </h2>
              <p className="text-muted fs-6 mx-auto" style={{ maxWidth: '680px', lineHeight: '1.7' }}>
                Whether you are launching a new software product, automating an internal business process, or modernizing your cloud setup, we provide clear engineering solutions tailored to your goals.
              </p>
            </div>
          </div>

          <ServiceCardsSection />

        </div>
      </section>

      {/* SECTION 5: WHY SEIKURAL — temporarily commented out
      <section className="py-5 sipl-section-transparent" aria-label="Why Seikural" style={{ position: 'relative', zIndex: 1 }}>
        <div className="container py-4">
          <div className="row g-5">

            <div className="col-12 col-lg-4 sipl-reveal-ttb">
              <span className="text-uppercase fw-bold sipl-text-teal tracking-wider small d-block mb-2" style={{ letterSpacing: '0.12em' }}>
                WHY WORK WITH US
              </span>
              <h2 className="display-6 fw-bold text-dark mb-3">
                A straightforward team focused on real results.
              </h2>
              <p className="text-muted fs-6" style={{ lineHeight: '1.8' }}>
                Building good software isn't about using trendy buzzwords. It's about listening to your needs, designing clean user flows, and shipping reliable code that solves real business problems.
              </p>
            </div>

            <div className="col-12 col-lg-7 offset-lg-1">
              <div className="row g-4">
                {WHY_POINTS.map((p, i) => {
                  const dirClass = i % 2 === 0 ? 'sipl-reveal-ltr' : 'sipl-reveal-rtl';
                  return (
                    <div key={p.num} className={`col-12 col-sm-6 ${dirClass}`} style={{ transitionDelay: `${i * 0.1}s` }}>
                      <div className="card h-100 border-0 shadow-sm rounded-4 p-4 sipl-card-hover" style={{ background: '#ffffff' }}>
                        <div className="card-body p-0">
                          <span className={`badge ${p.badgeClass} text-white px-2.5 py-1 mb-3 rounded`}>
                            {p.num}
                          </span>
                          <h3 className="h6 fw-bold text-dark mb-2">{p.title}</h3>
                          <p className="text-muted small mb-0" style={{ lineHeight: '1.6' }}>{p.desc}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

          </div>
        </div>
      </section>
      */}

      {/* SECTION 6: HOW WE WORK (PROCESS) */}
      <HowWeWorkSection />

      {/* SECTION 7: INDUSTRIES */}
      <IndustriesSection />


      {/* SECTION 8: FEATURED CAPABILITY SHOWCASE — temporarily commented out
      <section className="py-5 sipl-section-transparent" aria-label="Capability Showcase" style={{ position: 'relative', zIndex: 1 }}>
        <div className="container py-4">
          <div className="sipl-reveal-btt">
            <div className="card border-0 shadow-sm rounded-4 p-4 p-md-5" style={{ background: 'linear-gradient(135deg, #1a2f6e 0%, #0d9488 100%)', color: '#ffffff' }}>
              <div className="row align-items-center g-4">
                <div className="col-12 col-lg-8">
                  <span className="text-uppercase fw-bold text-light tracking-wider small d-block mb-2" style={{ opacity: 0.9, letterSpacing: '0.12em' }}>
                    READY TO BUILD?
                  </span>
                  <h2 className="display-6 fw-bold text-white mb-3">
                    Let's Turn Your Software Vision into Production Code
                  </h2>
                  <p className="text-light mb-0" style={{ opacity: 0.95, lineHeight: '1.8', fontSize: '1.05rem' }}>
                    We combine engineering rigor, user-focused design, and transparent project delivery to build web software that supports your business long into the future.
                  </p>
                </div>
                <div className="col-12 col-lg-4 text-center text-lg-end">
                  <Link to="/contact" className="btn btn-light text-dark fw-bold px-4 py-3 rounded-3 shadow-sm d-inline-block">
                    Start a Conversation
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      */}

      {/* SECTION 9: FINAL CTA */}
      <section className="py-5 sipl-section-transparent text-center position-relative border-top" aria-label="Final Call to Action" style={{ position: 'relative', zIndex: 1 }}>
        <div className="sipl-color-bar position-absolute top-0 start-0" />
        <div className="container py-5">
          <div className="row justify-content-center">
            <div className="col-12 col-lg-8 sipl-reveal-ttb">
              <span className="text-uppercase fw-bold sipl-text-navy tracking-wider small d-block mb-3" style={{ letterSpacing: '0.12em' }}>
                START A CONVERSATION
              </span>
              <h2 className="display-5 fw-bold text-dark mb-3">
                Have a project in mind or an operational challenge to solve?
              </h2>
              <p className="text-muted fs-6 mb-4" style={{ lineHeight: '1.8', maxWidth: '680px', margin: '0 auto 1.8rem auto' }}>
                Reach out for a direct, no-obligation consultation. We'll discuss your requirements, explain your tech options, and suggest a practical path forward—with no sales pressure.
              </p>
              <div className="d-flex flex-column flex-sm-row justify-content-center align-items-center gap-3">
                <CtaButton to="/contact" variant="primary">
                  Talk to Our Technical Team
                </CtaButton>
                <CtaButton to="/services/web-development" variant="secondary">
                  View All Services
                </CtaButton>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
